// 📄 2. src/services/authService.js
import axios from 'axios';
import { navigateOrRedirect } from '../utils/desktopNavigation';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';

class AuthService {
  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests automatically
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only redirect if user is actually logged in (has a token)
          // Don't redirect on failed login attempts.
          //
          // Always land on the app's OWN sign-in page ("/") rather than the
          // external marketing landing. (This was already the de-facto
          // behavior: the old code tested `isDesktopRuntime` as a bare
          // function reference — always truthy — so the marketing redirect
          // branch was dead code. Hard external bounces on transient 401s
          // are also what caused the May 2026 sign-in bounce regression.)
          const token = localStorage.getItem('token');
          if (token) {
            localStorage.removeItem('token');
            localStorage.removeItem('auth_token');
            navigateOrRedirect('/');
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async login(email, password) {
    try {
      // Use real authentication endpoints
      const response = await this.api.post('/auth/login', { email, password });
      return response.data;
    } catch (error) {
      // Include failed attempts info in the error for the login form
      const errorData = error.response?.data || {};
      const err = new Error(errorData.message || 'Login failed');
      err.code = errorData.code;
      err.ssoStartUrl = errorData.ssoStartUrl;
      err.failedAttempts = errorData.failedAttempts;
      err.suggestPasswordReset = errorData.suggestPasswordReset;
      err.accountLocked = errorData.accountLocked;
      throw err;
    }
  }

  async signup(userData) {
    try {
      // Use real authentication endpoints
      const response = await this.api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      // Check if this is an EMAIL_EXISTS case (409 status)
      if (error.response?.status === 409 && error.response?.data?.code === 'EMAIL_EXISTS') {
        // Return special response for email exists case
        const err = new Error(error.response.data.message);
        err.code = 'EMAIL_EXISTS';
        err.hasProductAccess = error.response.data.hasProductAccess;
        err.requestedProduct = error.response.data.requestedProduct;
        throw err;
      }
      const errorData = error.response?.data || {};
      const err = new Error(errorData.message || 'Signup failed');
      err.code = errorData.code;
      err.ssoStartUrl = errorData.ssoStartUrl;
      throw err;
    }
  }

  async validateToken(token) {
    try {
      // Bypass any HTTP cache that might short-circuit /auth/me with an
      // empty 304 response. Background:
      //   Express used to auto-emit weak ETags on JSON responses, so a
      //   browser that had cached a previous /auth/me hit would send
      //   `If-None-Match` and get a `304 Not Modified` with NO body
      //   back. axios then resolved `response.data` to `""`, this
      //   function returned `undefined`, `setUser(undefined)` ran, and
      //   `ProtectedRoute` immediately bounced the visitor back to the
      //   sign-in page — incognito mode worked because it has no
      //   persistent HTTP cache. The backend has since disabled ETags
      //   and sends `Cache-Control: no-store` on /auth/*, but a stale
      //   cache entry from before that fix can still ship a 304 from
      //   the browser's own cache. The cache-bust query param + no-cache
      //   headers below guarantee the request reaches origin and
      //   returns a full body.
      const response = await this.api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
        params: { _t: Date.now() },
      });
      // Diagnostic instrumentation for the May 27 2026 sign-in bounce.
      // Always-on console line so users stuck in the bounce can hand us
      // a precise snapshot of what the dashboard saw from /auth/me.
      try {
        // eslint-disable-next-line no-console
        console.info('[XPH-DEBUG-AUTH] /auth/me responded', {
          status: response?.status,
          hasData: Boolean(response?.data),
          hasUserField: Boolean(response?.data?.user),
          userIdPreview: response?.data?.user?.id ? String(response.data.user.id).slice(0, 12) + '…' : null,
          email: response?.data?.user?.email || null,
          accountType: response?.data?.user?.accountType || null,
          productAccess: response?.data?.user?.productAccess || null,
          signupProduct: response?.data?.user?.signupProduct || null,
        });
      } catch {
        // never let logging break auth
      }
      const user = response?.data?.user;
      if (!user || typeof user !== 'object') {
        const err = new Error('Token validation returned empty response');
        // Treat empty payload as a transient/cache failure, not a 401.
        // statusCode 0 keeps the AuthContext catch branch from clearing
        // the local session (it only clears on explicit 401/403).
        err.statusCode = 0;
        throw err;
      }
      return user;
    } catch (error) {
      try {
        // eslint-disable-next-line no-console
        console.error('[XPH-DEBUG-AUTH] /auth/me threw', {
          message: error?.message,
          status: error?.response?.status,
          statusCode: error?.statusCode,
          isCors: !error?.response, // network/CORS failures have no response
        });
      } catch {
        // never let logging break auth
      }
      const err = new Error(error?.message || 'Token validation failed');
      err.statusCode = error?.statusCode || error?.response?.status || 0;
      throw err;
    }
  }

  async refreshToken() {
    try {
      const response = await this.api.post('/auth/refresh');
      return response.data;
    } catch (error) {
      throw new Error('Token refresh failed');
    }
  }

  async getThemePreference() {
    const response = await this.api.get('/auth/theme-preference');
    return response.data;
  }

  async updateThemePreference(theme) {
    const response = await this.api.patch('/auth/theme-preference', { theme });
    return response.data;
  }

  // Persist the user's profile avatar. Pass a base64 image data URL, or null
  // to remove the current photo. Returns { success, avatar }.
  async updateAvatar(avatar) {
    const response = await this.api.patch('/auth/avatar', { avatar });
    return response.data;
  }
}

export const authService = new AuthService();
